import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  Download, Mail, FileText, Printer, ChevronLeft, Calendar, Hash,
  CheckCircle2, AlertTriangle, ArrowUpRight, Clock, User, Briefcase
} from "lucide-react";

const SECTIONS = [
  { key: "technical", label: "Technical Competency", color: "#002FA7" },
  { key: "soft_skills", label: "Soft Skills", color: "#60A5FA" },
  { key: "cultural_fit", label: "Cultural Fit", color: "#10B981" },
  { key: "experience", label: "Experience", color: "#F59E0B" },
  { key: "personality", label: "Personality", color: "#FF3B30" },
];

const STATUS_OPTIONS = [
  { v: "pending",      l: "Pending",      c: "border-[#F59E0B] bg-[#F59E0B]/5 text-[#92400E]" },
  { v: "selected",     l: "Selected",     c: "border-[#10B981] bg-[#10B981]/5 text-[#065F46]" },
  { v: "next_round",   l: "Next round",   c: "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" },
  { v: "not_selected", l: "Not selected", c: "border-[#FF3B30] bg-[#FF3B30]/5 text-[#7F1D1D]" },
];

const recommendationStyle = (rec = "") => {
  const r = rec.toLowerCase();
  if (r.includes("strong hire")) return { bg: "#10B981", fg: "#FFFFFF", icon: CheckCircle2 };
  if (r.includes("hire"))         return { bg: "#002FA7", fg: "#FFFFFF", icon: CheckCircle2 };
  if (r.includes("no hire"))      return { bg: "#FF3B30", fg: "#FFFFFF", icon: AlertTriangle };
  return { bg: "#F59E0B", fg: "#FFFFFF", icon: Clock };
};

export default function InterviewReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedQ, setExpandedQ] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/interviews/${id}`);
    setData(data);
    setStatus(data.hr_status || "pending");
    setNote(data.hr_note || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.post(`/interviews/${id}/status`, { hr_status: status, note });
      toast.success("Status updated — candidate emailed.");
      await load();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const feedback = useMemo(() => {
    try { return data?.feedback ? JSON.parse(data.feedback) : {}; } catch { return {}; }
  }, [data]);

  const pieData = useMemo(() => {
    if (!data?.scores) return [];
    return SECTIONS.map((s) => ({
      name: s.label, value: Number(data.scores?.[s.key] ?? 0), color: s.color, key: s.key
    })).filter(d => d.value > 0);
  }, [data]);

  const buildTranscriptText = () => {
    if (!data) return "";
    const sep = "═".repeat(60);
    const lines = [];
    lines.push("SCOREBAR · INTERVIEW REPORT");
    lines.push(sep);
    lines.push(`Code:        ${data.code}`);
    lines.push(`Role:        ${data.role}`);
    lines.push(`Candidate:   ${data.candidate_name || "—"} <${data.candidate_email}>`);
    lines.push(`Format:      ${data.interview_type}${data.category ? ` · ${data.category}` : ""}`);
    lines.push(`Status:      ${data.status}`);
    lines.push(`HR decision: ${data.hr_status || "pending"}`);
    lines.push("");
    lines.push(`OVERALL SCORE: ${data.overall ?? 0} / 100`);
    if (feedback.recommendation) lines.push(`Recommendation: ${feedback.recommendation}`);
    lines.push("");
    lines.push("SCORE BREAKDOWN");
    SECTIONS.forEach((s) => {
      const v = data.scores?.[s.key] ?? 0;
      lines.push(`  · ${s.label.padEnd(28)} ${String(v).padStart(3)}/100`);
    });
    if (feedback.summary) { lines.push(""); lines.push("SUMMARY"); lines.push(feedback.summary); }
    if (feedback.strengths?.length) { lines.push(""); lines.push("STRENGTHS"); feedback.strengths.forEach((s) => lines.push(`  + ${s}`)); }
    if (feedback.weaknesses?.length) { lines.push(""); lines.push("AREAS TO IMPROVE"); feedback.weaknesses.forEach((s) => lines.push(`  - ${s}`)); }
    if (data.questions?.length) {
      lines.push(""); lines.push("PER-QUESTION TRANSCRIPT"); lines.push("-".repeat(60));
      data.questions.forEach((q, i) => { lines.push(""); lines.push(`Q${i + 1}. ${q.q}`); lines.push(`A:  ${q.a || "—"}`); });
    } else if (data.transcript) {
      lines.push(""); lines.push("FULL TRANSCRIPT"); lines.push("-".repeat(60)); lines.push(data.transcript);
    }
    lines.push(""); lines.push(sep);
    lines.push(`Generated by Scorebar AI · ${new Date().toLocaleString()}`);
    return lines.join("\n");
  };

  const downloadTranscript = () => {
    const text = buildTranscriptText();
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const safeCode = (data?.code || "interview").replace(/[^A-Za-z0-9-_]/g, "_");
    const a = document.createElement("a");
    a.href = url; a.download = `Scorebar_${safeCode}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Transcript downloaded");
  };

  const shareViaGmail = () => {
    if (!data) return;
    const subject = `Scorebar Interview Report — ${data.candidate_name || data.candidate_email} · ${data.role}`;
    const body = buildTranscriptText();
    const to = data.candidate_email || "";
    const url = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const popup = window.open(url, "_blank", "noopener,noreferrer");
    if (!popup) toast.error("Pop-up blocked. Allow pop-ups and try again.");
    else toast.success("Opening Gmail…");
  };

  const printReport = () => window.print();

  if (!data) return <div className="p-12 text-sm text-gray-500">Loading…</div>;
  const overall = data.overall || 0;
  const isHR = user?.role === "hr";
  const recStyle = recommendationStyle(feedback.recommendation || "");
  const RecIcon = recStyle.icon;
  const completedAt = data.completed_at ? new Date(data.completed_at) : (data.created_at ? new Date(data.created_at) : null);
  const initials = (data.candidate_name || data.candidate_email || "?").split(/[\s.@]/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const currentStatus = STATUS_OPTIONS.find(s => s.v === (data.hr_status || "pending"));

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Navbar/>

      {/* Sub-header / breadcrumb */}
      <div className="bg-white border-b border-gray-200 print:hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-3 flex items-center justify-between gap-4 flex-wrap">
          <Link to={isHR ? "/hr" : "/"} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#002FA7] transition-colors" data-testid="back-link">
            <ChevronLeft className="w-4 h-4"/> {isHR ? "Back to dashboard" : "Back home"}
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            {currentStatus && (
              <span className={`overline px-2.5 py-1 border ${currentStatus.c}`} data-testid="header-status-pill">
                {currentStatus.l}
              </span>
            )}
            {isHR && (
              <>
                <button onClick={printReport} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 hover:border-gray-900 hover:bg-white transition-colors" data-testid="print-button">
                  <Printer className="w-3.5 h-3.5"/> Print
                </button>
                <button onClick={downloadTranscript} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 hover:border-gray-900 hover:bg-white transition-colors" data-testid="download-transcript">
                  <Download className="w-3.5 h-3.5"/> Download
                </button>
                <button onClick={shareViaGmail} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#002FA7] text-white border border-[#002FA7] hover:bg-[#00227B] transition-colors" data-testid="share-gmail">
                  <Mail className="w-3.5 h-3.5"/> Share via Gmail
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-10" data-testid="report-page">

        {/* Candidate header */}
        <header className="bg-white border border-gray-200 p-7 lg:p-8 mb-8" data-testid="report-header">
          <div className="grid lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-8 flex items-start gap-5">
              <div className="w-16 h-16 shrink-0 bg-[#111827] text-white flex items-center justify-center font-display text-xl font-bold">
                {initials || <User className="w-7 h-7"/>}
              </div>
              <div className="min-w-0">
                <div className="overline text-[#002FA7]">// Interview report</div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tighter text-[#111827] mt-1">
                  {data.candidate_name || data.candidate_email}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-gray-400"/>{data.role}</span>
                  <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400"/>{data.candidate_email}</span>
                  <span className="inline-flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-gray-400"/><span className="font-mono text-xs">{data.code}</span></span>
                  {completedAt && <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400"/>{completedAt.toLocaleDateString(undefined, {year:"numeric", month:"short", day:"numeric"})}</span>}
                  <span className="overline text-gray-400 capitalize">{data.interview_type}{data.category ? ` · ${data.category}` : ""}</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              {feedback.recommendation && (
                <div
                  className="inline-flex items-center gap-2 px-4 py-2.5 font-medium text-sm shadow-[3px_3px_0_0_rgba(17,24,39,1)]"
                  style={{ background: recStyle.bg, color: recStyle.fg }}
                  data-testid="recommendation-badge"
                >
                  <RecIcon className="w-4 h-4" strokeWidth={2.2}/>
                  {feedback.recommendation}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Score panel */}
        <section className="grid lg:grid-cols-12 gap-6 mb-8" data-testid="score-panel">
          {/* Overall + Pie */}
          <div className="lg:col-span-7 bg-white border border-gray-200">
            <div className="border-b border-gray-200 px-7 py-4 flex items-center justify-between">
              <div className="overline text-[#002FA7]">// Score breakdown</div>
              <div className="text-xs text-gray-500">5-section rubric</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 p-7 items-center">
              <div className="h-64 relative" data-testid="score-pie">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={62}
                        outerRadius={102}
                        stroke="#FFFFFF"
                        strokeWidth={3}
                        paddingAngle={1}
                        isAnimationActive={true}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color}/>
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{border:"1px solid #111827", borderRadius:0, fontFamily:"IBM Plex Sans", fontSize:12, padding:"6px 10px"}}
                        formatter={(v, n) => [`${v}/100`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-sm text-gray-500">No scores yet.</div>
                )}
                {pieData.length > 0 && (
                  <div className="absolute inset-0 grid place-items-center pointer-events-none">
                    <div className="text-center">
                      <div className="font-display text-4xl lg:text-5xl font-bold tracking-tighter text-[#111827]">{overall}</div>
                      <div className="text-[10px] tracking-[0.2em] text-gray-500 uppercase mt-0.5">/ 100</div>
                    </div>
                  </div>
                )}
              </div>
              <div data-testid="pie-legend">
                <ul className="divide-y divide-gray-100">
                  {SECTIONS.map((s) => {
                    const v = data.scores?.[s.key] ?? 0;
                    return (
                      <li key={s.key} className="py-2.5 flex items-center justify-between gap-4" data-testid={`pie-row-${s.key}`}>
                        <span className="flex items-center gap-2.5 text-sm text-gray-700">
                          <span className="w-2.5 h-2.5 rounded-full" style={{background:s.color}}/>
                          {s.label}
                        </span>
                        <span className="font-mono text-sm font-medium text-[#111827]">{v}<span className="text-gray-400">/100</span></span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Per-section detail */}
          <div className="lg:col-span-5 bg-white border border-gray-200">
            <div className="border-b border-gray-200 px-7 py-4">
              <div className="overline text-[#002FA7]">// Detail</div>
            </div>
            <div className="p-7 space-y-5" data-testid="scores-grid">
              {SECTIONS.map((s) => {
                const v = data.scores?.[s.key] ?? 0;
                return (
                  <div key={s.key} data-testid={`score-${s.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-[#111827]">{s.label}</span>
                      <span className="font-mono text-sm font-semibold" style={{color:s.color}}>{v}<span className="text-gray-400 font-normal">/100</span></span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{width:`${v}%`, background:s.color}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Summary + Strengths/Weaknesses */}
        <section className="grid lg:grid-cols-12 gap-6 mb-8">
          {feedback.summary && (
            <div className="lg:col-span-12 bg-white border border-gray-200 p-7" data-testid="summary-card">
              <div className="overline text-[#002FA7] mb-3 inline-flex items-center gap-2"><FileText className="w-3.5 h-3.5"/> Executive summary</div>
              <p className="text-base leading-relaxed text-gray-800">{feedback.summary}</p>
            </div>
          )}

          {(feedback.strengths?.length || feedback.weaknesses?.length) ? (
            <>
              {feedback.strengths?.length > 0 && (
                <div className="lg:col-span-6 bg-white border border-gray-200 p-7" data-testid="strengths-card">
                  <div className="overline text-[#10B981] mb-4 inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5"/> Strengths</div>
                  <ul className="space-y-3">
                    {feedback.strengths.map((s,i)=>(
                      <li key={i} className="flex gap-3 text-sm text-gray-800 leading-relaxed">
                        <span className="text-[#10B981] mt-0.5">✓</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {feedback.weaknesses?.length > 0 && (
                <div className="lg:col-span-6 bg-white border border-gray-200 p-7" data-testid="weaknesses-card">
                  <div className="overline text-[#FF3B30] mb-4 inline-flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5"/> Areas to improve</div>
                  <ul className="space-y-3">
                    {feedback.weaknesses.map((s,i)=>(
                      <li key={i} className="flex gap-3 text-sm text-gray-800 leading-relaxed">
                        <span className="text-[#FF3B30] mt-0.5">!</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </section>

        {/* HR Decision panel */}
        {isHR && (
          <section className="bg-white border border-gray-200 p-7 mb-8" data-testid="hr-status-panel">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div>
                <div className="overline text-[#002FA7] mb-1">// HR decision</div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#111827]">Update candidate status</h3>
                <p className="text-sm text-gray-500 mt-1">The selected status will be emailed to the candidate.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {STATUS_OPTIONS.map(o => (
                <button
                  key={o.v}
                  onClick={()=>setStatus(o.v)}
                  className={`border-2 p-4 text-left transition-all ${status===o.v ? `${o.c} font-medium` : "border-gray-200 hover:border-gray-400 text-gray-600"}`}
                  data-testid={`status-${o.v}`}
                >
                  <div className="text-sm font-medium">{o.l}</div>
                  {status === o.v && <div className="overline mt-1 opacity-80">selected</div>}
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              value={note}
              onChange={(e)=>setNote(e.target.value)}
              placeholder="Optional note to the candidate (will appear in the email)"
              className="mt-5 w-full border border-gray-300 p-3 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
              data-testid="hr-note"
            />
            <div className="mt-4 flex justify-end">
              <button onClick={saveStatus} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="save-status">
                {saving ? "Saving…" : <>Update & email candidate <ArrowUpRight className="w-4 h-4"/></>}
              </button>
            </div>
          </section>
        )}

        {/* Video full session */}
        {data.video_url && (
          <section className="bg-white border border-gray-200 p-7 mb-8" data-testid="report-video-section">
            <div className="overline text-gray-500 mb-4">// Full session video</div>
            <video controls className="w-full max-w-3xl border border-gray-900" src={`${API.replace('/api','')}${data.video_url}`} data-testid="report-video"/>
          </section>
        )}

        {/* Per-question transcripts */}
        {data.questions?.length > 0 && (
          <section className="bg-white border border-gray-200 mb-8" data-testid="per-question-section">
            <div className="border-b border-gray-200 px-7 py-4 flex items-center justify-between">
              <div className="overline text-[#002FA7]">// Per-question transcript</div>
              <span className="text-xs text-gray-500">{data.questions.length} {data.questions.length === 1 ? "question" : "questions"}</span>
            </div>
            <div data-testid="per-question-clips">
              {data.questions.map((q, i) => (
                <div key={i} className="border-b border-gray-100 last:border-b-0" data-testid={`q-clip-${i}`}>
                  <button
                    onClick={() => setExpandedQ(expandedQ === i ? null : i)}
                    className="w-full px-7 py-4 flex items-start gap-4 text-left hover:bg-gray-50 transition-colors"
                    data-testid={`q-toggle-${i}`}
                  >
                    <span className="overline text-[#002FA7] mt-0.5 shrink-0">Q{i + 1}</span>
                    <span className="flex-1 font-medium text-[#111827]">{q.q}</span>
                    <span className="text-xs text-gray-400 shrink-0">{expandedQ === i ? "Hide" : "Show answer"}</span>
                  </button>
                  {expandedQ === i && (
                    <div className="px-7 pb-6 -mt-1 space-y-4">
                      {q.video_url && (
                        <video controls className="w-full max-w-xl border border-gray-900" src={`${API.replace('/api','')}${q.video_url}`}/>
                      )}
                      <div>
                        <div className="overline text-gray-500 mb-1.5">Transcript</div>
                        <p className="font-mono text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{q.a || "—"}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Full transcript fallback */}
        {(!data.questions || data.questions.length === 0) && data.transcript && (
          <section className="bg-white border border-gray-200 p-7 mb-8">
            <div className="overline text-gray-500 mb-3">// Full transcript</div>
            <div className="font-mono text-sm whitespace-pre-wrap text-gray-700 leading-relaxed" data-testid="report-transcript">{data.transcript}</div>
          </section>
        )}

        <div className="text-xs text-gray-400 text-center print:hidden">
          Generated by Scorebar AI · Evidence-based interviews
        </div>
      </main>
    </div>
  );
}
